import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoginPage from "./pages/LoginPage.jsx";
import DashboardPage from "./pages/DashboardPage.jsx";
import RestaurantPage from "./pages/RestaurantPage.jsx";
import AddRestaurantPage from "./pages/AddRestaurantPage.jsx";
import ClientAccountsPage from "./pages/ClientAccountsPage.jsx";
import TicketsPage from "./pages/TicketsPage.jsx";
import UpdateNotesPage from "./pages/UpdateNotesPage.jsx";
import Sidebar, { SidebarOverlay } from "./components/Sidebar.jsx";
import HubTopbar from "./components/HubTopbar.jsx";
import CommandPalette from "./components/CommandPalette.jsx";
import { hubApi, hubExt, calcFinalAmount } from "./api.js";

const isAuth = () => !!localStorage.getItem("hubToken");

const Protected = ({ children }) => {
  if (!isAuth()) return <Navigate to="/login" replace />;
  return children;
};

export default function App() {
  const loggedIn = isAuth();
  const [drawerOpen,  setDrawerOpen]  = useState(false);
  const [cmdOpen,     setCmdOpen]     = useState(false);
  const [stats,       setStats]       = useState({});
  const [restaurants, setRestaurants] = useState([]);

  // Global stats fetch (runs once on login)
  const fetchStats = useCallback(async () => {
    if (!isAuth()) return;
    try {
      const [restRes, tickRes] = await Promise.allSettled([
        hubApi.getAll(),
        hubExt.getAllTickets(),
      ]);
      const rests = restRes.status === "fulfilled" ? (restRes.value.data.restaurants || []) : [];
      const tickets = tickRes.status === "fulfilled" ? (tickRes.value.data.tickets || []) : [];
      setRestaurants(rests);

      const active = rests.filter(r => r.isActive);
      const mrr = active.reduce((sum, r) => {
        const base = calcFinalAmount(r.plan, r.billingPeriod || "monthly", r.discountType, r.discountValue);
        const m = r.billingPeriod === "yearly" ? base / 12 : r.billingPeriod === "quarterly" ? base / 3 : base;
        return sum + m;
      }, 0);

      setStats({
        activeCount:     active.length,
        totalCount:      rests.length,
        mrr,
        overdueCount:    rests.filter(r => r.billingStatus === "overdue").length,
        openTickets:     tickets.filter(t => t.status === "open").length,
        pendingAccounts: 0, // will be filled if we fetch accounts
      });
    } catch {}
  }, []);

  useEffect(() => {
    if (loggedIn) fetchStats();
  }, [loggedIn, fetchStats]);

  // Global ⌘K listener
  useEffect(() => {
    const fn = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setCmdOpen(o => !o); } };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, []);

  if (!loggedIn) {
    return (
      <>
        <ToastContainer position="top-right" theme="light" />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*"     element={<Navigate to="/login" replace />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <ToastContainer position="top-right" theme="light" />
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} restaurants={restaurants} />

      <div id="hub-shell">
        <Sidebar open={drawerOpen} onClose={() => setDrawerOpen(false)} stats={stats} />
        <SidebarOverlay show={drawerOpen} onClose={() => setDrawerOpen(false)} />

        <div id="hub-main">
          <HubTopbar
            stats={stats}
            onHamburger={() => setDrawerOpen(true)}
            onOpenCmd={() => setCmdOpen(true)}
          />
          <Routes>
            <Route path="/"                   element={<Protected><DashboardPage /></Protected>} />
            <Route path="/restaurants/new"    element={<Protected><AddRestaurantPage /></Protected>} />
            <Route path="/restaurants/:id"    element={<Protected><RestaurantPage /></Protected>} />
            <Route path="/client-accounts"    element={<Protected><ClientAccountsPage /></Protected>} />
            <Route path="/tickets"            element={<Protected><TicketsPage /></Protected>} />
            <Route path="/update-notes"       element={<Protected><UpdateNotesPage /></Protected>} />
            <Route path="*"                   element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </>
  );
}
